import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  BackdropForDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OnboardingStatusDots,
  SubmitButton,
} from 'components';
import { EOnboardingStep } from 'enums';
import { BuildingForm } from 'forms';
import { useOnboardingContext } from './onboarding.context.hook';
import { IBuilding } from 'models';
import { DialogPrevNextClose } from './dialog-prev-next-close';

interface OnboardingBuildingDialogProps {
  visible?: boolean;
}

export const OnboardingBuildingDialog = ({ visible = false }: OnboardingBuildingDialogProps) => {
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading_form, setLoadingForm] = useState<boolean>(false);
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);
  const { setVisible, buildingId, updateOnboarding } = useOnboardingContext();

  const formRef = React.useRef<any>();

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  const onNext = buildingId
    ? () => {
        updateOnboarding({ status: EOnboardingStep.SETUP_ZONE });
      }
    : onSubmit;

  const next = (building?: IBuilding) => {
    const buildingId = building?.id;

    if (buildingId) {
      updateOnboarding({ status: EOnboardingStep.SETUP_ZONE, buildingId });
    }
  };

  const title = 'Let’s set up your first job site.';
  // const subtitle = 'This helps us build your interactive dashboard.';
  const subtitle = '';
  const submitButtonLabel = 'Set Up a Zone Next';

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <DialogPrevNextClose onNext={onNext} onClose={onClose} />

        <Box>
          <Typography variant="subtitle2" color="textSecondary">
            Step 1 of 6
          </Typography>
        </Box>

        <Box sx={{ marginTop: 2 }}>
          <Typography variant="h5">{title}</Typography>

          <Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box paddingTop={2}>
          <BuildingForm
            ref={formRef}
            buildingId={buildingId}
            onInit={(loading) => setLoadingForm(loading)}
            onSubmitting={(loading) => setLoadingSubmit(loading)}
            next={next}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <SubmitButton loading={loading_submit} label={submitButtonLabel} onClick={onSubmit} />
      </DialogActions>

      <Box
        sx={{
          width: '100%',
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
        }}
      >
        <OnboardingStatusDots status={EOnboardingStep.SETUP_BUILDING} />
      </Box>

      <BackdropForDialog loading={loading_form} />
    </Dialog>
  );
};
